import { useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Upload, X, FileText, AlertTriangle, RotateCcw } from "lucide-react";
import { cn } from "@tellescope/utils";
import { Button } from "@tellescope/ui";
import { Card } from "@tellescope/ui";
import { TellescopeFormFieldBaseProps } from "../types";
import { useTellescopeInfiniteQuery, useTellescopeMutation, useUploadTellescopeFile } from "@tellescope/tanstack-query";

export interface FileFieldExtraProps {
  multiple?: boolean;
  allowedTypes?: string; // MIME types or file extensions (e.g., "image/*,.pdf" or ".jpg,.png")
  maxSizeMB?: number; // Maximum file size in MB (default: 1024 = 1GB)
  enduserId: string;
}

export interface FileFieldProps extends TellescopeFormFieldBaseProps<"files">, FileFieldExtraProps {}

export const FileField = ({
  title,
  value = [],
  onChange,
  className,
  allowedTypes,
  multiple = false,
  maxSizeMB = 1024,
  enduserId,
  error: validationError,
}: FileFieldProps) => {
  const { t } = useTranslation("forms");
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [erroredFiles, setErroredFiles] = useState<File[]>([]);
  const [filesBeingUploaded, setFilesBeingUploaded] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const secureNames = value.map(({ secureName }) => secureName);

  const uploadedFilesQuery = useTellescopeInfiniteQuery({
    model: "files",
    method: "getSome",
    request: {
      filter: {
        secureName: {
          _in: secureNames,
        },
      },
    },
    select: (data) => data.pages.flat(),
  });
  const uploadedFiles = useMemo(() => {
    const sortable = [...(uploadedFilesQuery.data ?? value)];
    return sortable.sort((a, b) => a.name.localeCompare(b.name));
  }, [uploadedFilesQuery.data, value]);

  const deleteFileMutation = useTellescopeMutation({
    model: "files",
    method: "deleteOne",
    autoCacheUpdateStrategy: "update",
  });

  const { uploadFileAsync } = useUploadTellescopeFile();

  // Parse allowedTypes to support both MIME types and file extensions
  const parseAllowedTypes = (types: string): { mimeTypes: string[]; extensions: string[] } => {
    const parts = types.split(",").map((t) => t.trim());
    const mimeTypes: string[] = [];
    const extensions: string[] = [];

    parts.forEach((part) => {
      if (part.startsWith(".")) {
        extensions.push(part.toLowerCase());
      } else {
        mimeTypes.push(part.toLowerCase());
      }
    });

    return { mimeTypes, extensions };
  };

  // Validate file type
  const isFileTypeAllowed = (file: File): boolean => {
    if (!allowedTypes) return true;

    const { mimeTypes, extensions } = parseAllowedTypes(allowedTypes);
    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();

    // Check MIME type patterns (e.g., "image/*")
    const mimeTypeMatches = mimeTypes.some((type) => {
      if (type.endsWith("/*")) {
        const baseType = type.slice(0, -2);
        return fileType.startsWith(baseType);
      }
      return fileType === type;
    });

    // Check file extensions
    const extensionMatches = extensions.some((ext) => fileName.endsWith(ext));

    return mimeTypeMatches || extensionMatches;
  };

  // Validate file size
  const isFileSizeValid = (file: File): boolean => {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    return file.size <= maxSizeBytes;
  };

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} bytes`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Handle file selection
  const handleNewFiles = async (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const validFiles: File[] = [];
    let errorMessage: string | null = null;

    for (const file of fileArray) {
      if (!isFileTypeAllowed(file)) {
        errorMessage = t("Invalid file type");
        break;
      }
      if (!isFileSizeValid(file)) {
        errorMessage = t("File too large (max {{maxSize}}MB)", { maxSize: maxSizeMB });
        break;
      }
      validFiles.push(file);
    }

    if (errorMessage) {
      setError(errorMessage);
      return;
    }

    setError(null);
    setFilesBeingUploaded(validFiles);

    const responses = await Promise.all(
      validFiles.map(async (file) => {
        try {
          const res = await uploadFileAsync({ enduserId, name: file.name, size: file.size, type: file.type }, file);
          return {
            uploadedFile: res,
            originalFile: file,
          };
        } catch {
          return {
            originalFile: file,
          };
        }
      })
    );

    const { uploadedFiles, erroredFiles } = responses.reduce<{
      uploadedFiles: Awaited<ReturnType<typeof uploadFileAsync>>[];
      erroredFiles: File[];
    }>(
      (acc, curr) => {
        if (curr.uploadedFile) {
          acc.uploadedFiles.push(curr.uploadedFile);
        } else {
          acc.erroredFiles.push(curr.originalFile);
        }
        return acc;
      },
      { uploadedFiles: [], erroredFiles: [] }
    );

    if (multiple) {
      onChange([
        ...(value || []),
        ...uploadedFiles.map((file) => ({
          name: file.name,
          secureName: file.secureName,
          type: file.type,
        })),
      ]);
    } else {
      const uploadedFile = uploadedFiles[0];
      if (!uploadedFile) {
        onChange([]);
      } else {
        onChange([
          {
            name: uploadedFile.name,
            secureName: uploadedFile.secureName,
            type: "file", // The tellescope validator expects 'file' for single field validation for some reason
          },
        ]);
      }
    }
    setErroredFiles(erroredFiles);
    setFilesBeingUploaded([]);
  };

  // Handle drag events
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleNewFiles(e.dataTransfer.files);
  };

  // Handle click to open file picker
  const handleClick = () => {
    fileInputRef.current?.click();
  };

  // Handle file input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleNewFiles(e.target.files);
    // Reset input so the same file can be selected again
    e.target.value = "";
  };

  // Remove a file from the list
  const handleRemoveFile = (secureName: string) => {
    const newFiles = value.filter((file) => secureName !== file.secureName);
    onChange(newFiles);
    const fileId = uploadedFilesQuery.data?.find((file) => file.secureName === secureName)?.id;
    if (fileId) {
      deleteFileMutation.mutate(fileId);
    }
    setError(null);
  };

  const handleRemoveErroredFile = (index: number) => {
    setErroredFiles((prev) => [...prev.slice(0, index), ...prev.slice(index + 1)]);
  };

  const fileListCount = uploadedFiles.length + filesBeingUploaded.length + erroredFiles.length;
  const showFileList = fileListCount > 0;

  return (
    <div className={cn("space-y-2", className)}>
      {!!title && <h4 className="text-sm font-medium">{title}</h4>}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        accept={allowedTypes}
        onChange={handleInputChange}
        className="hidden"
        aria-label={title || t("Choose files...")}
      />

      {/* Drag-drop zone */}
      <Card
        className={cn(
          "border-2 border-dashed cursor-pointer transition-colors",
          isDragging
            ? "border-primary bg-muted"
            : "border-muted-foreground/25 hover:border-muted-foreground/50 hover:bg-muted/50",
          validationError ? "border-destructive" : ""
        )}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <div className="flex flex-col items-center justify-center p-6 text-center">
          <Upload className="h-10 w-10 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            {multiple ? t("Drop files here or click to select") : t("Drop a file here or click to select")}
          </p>
          {allowedTypes && (
            <p className="text-xs text-muted-foreground mt-1">
              {t("Allowed types")}: {allowedTypes}
            </p>
          )}
          {maxSizeMB && (
            <p className="text-xs text-muted-foreground">
              {t("Max size")}: {maxSizeMB >= 1024 ? `${maxSizeMB / 1024} GB` : `${maxSizeMB} MB`}
            </p>
          )}
        </div>
      </Card>

      {/* Error message */}
      {(error || validationError) && <p className="text-sm text-destructive">{error || validationError}</p>}

      {/* Uploaded File list */}
      {showFileList && (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">{t("{{count}} file selected", { count: fileListCount })}</p>
          <div className="space-y-1">
            {uploadedFiles.map((file) => (
              <div key={file.secureName} className="flex items-center gap-2 p-2 border rounded-md bg-muted/30">
                <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{file.name}</p>
                  <p className="text-xs text-muted-foreground">{file.type}</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile(file.secureName);
                  }}
                  aria-label={t("Remove file")}
                  className="flex-shrink-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {filesBeingUploaded.map((file, i) => (
              <div
                key={`${file.name}-${i}`}
                className="flex items-center gap-2 p-2 border rounded-md bg-muted/30 opacity-70"
              >
                <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{file.name}</p>
                  <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                </div>
                <p className="text-sm text-muted-foreground">{t("Uploading...")}</p>
              </div>
            ))}
            {erroredFiles.map((file, i) => (
              <div
                key={`${file.name}-${i}-error`}
                className="flex items-center gap-2 p-2 border rounded-md bg-muted/30 border-red-500"
              >
                <AlertTriangle className="h-4 w-4 text-muted-foreground flex-shrink-0" color="red" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{file.name}</p>
                  <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNewFiles([file]);
                    }}
                    aria-label={t("Retry upload")}
                    className="flex-shrink-0"
                    color="red"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveErroredFile(i);
                    }}
                    aria-label={t("Remove file")}
                    className="flex-shrink-0"
                    color="red"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
